import { mapState } from 'vuex';
import ApiService from '@/services/apiService';
import { localizeDate } from '@/utils/date';
import { get } from 'lodash';
import { templateMixin } from '@/components/core/templateMixin';
import { versionsHasUnsyncedChanges } from '@/utils/deviceTemplateUtils';
export default {
  name: 'DeviceTemplatesList',
  mixins: [templateMixin],
  data() {
    return {
      selectedAccounts: [],
      accountFilterIsReady: false,
      cacheBuster: 0,
      itemIdToBeDeleted: null,
      showDialogCreate: false,
      showDialogCopy: false,
      templateToCopy: null,
      page: 1,
      itemsPerPage: 10,
      totalCountOfLinkedDevices: 0,
      linkedDevices: [],
      isLoading: false,
      itemToDelete: null,
      devicesHasNextPage: true
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'UiTheme', 'permissions']),
    isDeviceTemplateManager() {
      return this.permissions['DeviceTemplate.manage'];
    },
    tableHeaders() {
      const headers = [{
        text: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.tableHeaders.id'),
        value: 'id',
        sortable: true
      }, {
        text: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.tableHeaders.title'),
        value: 'title',
        isTitle: true,
        sortable: true
      }, {
        text: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.tableHeaders.deviceDriverId'),
        value: 'deviceDriverInfo',
        sortable: true
      }, {
        text: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.tableHeaders.account'),
        value: 'Tenant.name',
        sortable: true
      }, {
        text: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.tableHeaders.status'),
        value: 'deviceSyncStatus',
        isTemplateStatus: true,
        sortable: false
      }, {
        text: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.tableHeaders.createdAt'),
        value: 'createdAt',
        sortable: true
      }, {
        text: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.tableHeaders.updatedAt'),
        value: 'updatedAt',
        sortable: true
      }];
      if (this.isDeviceTemplateManager) {
        headers.push({
          text: this.$i18n_t('DeviceDriver.list.table.headers.actions'),
          isButtons: true,
          width: '140px',
          align: 'center',
          sortable: false,
          buttons: [{
            icon: 'fal fa-edit',
            color: this.color,
            dataCy: 'edit_template',
            onClick: item => {
              this.$router.push(this.getSingleTemplateUrl(item.id));
            }
          }, {
            icon: 'fal fa-copy',
            color: this.color,
            dataCy: 'copy_template',
            onClick: item => {
              this.templateToCopy = item;
              this.showDialogCopy = true;
            }
          }, {
            icon: 'fal fa-trash-alt',
            color: this.color,
            onClick: item => {
              this.itemToDelete = item;
              this.devicesHasNextPage = true;
              this.fetchLinkedVirtualDevices(item);
              this.onDeleteClick(item);
            }
          }]
        });
      }
      return headers;
    },
    deletePromptTitle() {
      if (this.totalCountOfLinkedDevices > 0) {
        return this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.linkedVirtualDevicesTitle', {
          count: this.totalCountOfLinkedDevices
        });
      } else {
        return this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.deleteDialog.title');
      }
    },
    deletePromptDescription() {
      if (this.totalCountOfLinkedDevices > 0) {
        return this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.linkedVirtualDevicesDescription');
      } else {
        return this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.deleteDialog.message');
      }
    }
  },
  methods: {
    get,
    localizeDate,
    finishedCreating() {
      this.showDialogCreate = false;
    },
    finishedCopying(template) {
      this.showDialogCopy = false;
      this.templateToCopy = null;
      this.cacheBuster += 1;
      this.$router.push(this.getSingleTemplateUrl(template.id));
    },
    closeCopyDialog() {
      this.showDialogCopy = false;
      this.templateToCopy = null;
    },
    getSingleTemplateUrl(id) {
      return `/device-templates/${id}?tab=tab-device-meta`;
    },
    async fetchItems(options) {
      const {
        search,
        page,
        perPage,
        sortBy,
        sortDirection,
        selectedAccounts
      } = options;
      let searchItems = [];
      if (search && !isNaN(search)) {
        searchItems.push({
          variable: 'id',
          search: parseInt(search)
        });
      } else if (search) {
        searchItems.push({
          variable: 'title',
          search: search
        });
        searchItems.push({
          variable: 'devicedriver.title',
          search: search
        });
      }
      const tenantIds = selectedAccounts.map(account => account.id);
      try {
        const result = await ApiService.deviceTemplatesFetch({
          page: page,
          pageSize: perPage,
          sortBy: sortBy,
          sortDesc: sortDirection === 'desc',
          searchItems,
          tenantIds
        });
        return {
          items: result.data.map(item => {
            const version = item.DraftVersion || item.ProductiveVersion;
            let accountName = version.tenantId;
            if (version.account && version.account.name) {
              accountName = version.account.name;
            }
            const driverTitle = version.devicedriver ? version.devicedriver.title : '—';
            return {
              ...version,
              account: {
                ...version.account,
                name: accountName
              },
              createdAt: this.localizeDate(version.createdAt),
              updatedAt: this.localizeDate(version.updatedAt),
              hasUnsyncedChanges: versionsHasUnsyncedChanges(item),
              deviceDriverInfo: `${driverTitle} (${version.deviceDriverVersion})`
            };
          }),
          total: result.pagination['total-count']
        };
      } catch (error) {
        console.error(error);
        this.$snack(this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.snacks.fetchError'), 'error');
      }
    },
    onDeleteClick(item) {
      this.itemIdToBeDeleted = item.id;
    },
    async onDeleteItemConfirm() {
      const itemId = this.itemIdToBeDeleted;
      try {
        await ApiService.deviceTemplateDelete(itemId);
        this.$snack(this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.snacks.deleteSuccess'), 'success');
        this.cacheBuster += 1;
      } catch (error) {
        console.error(error);
        this.$snack(this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.snacks.deleteError'), 'error');
      } finally {
        this.itemIdToBeDeleted = null;
      }
    },
    async fetchLinkedVirtualDevices(item) {
      if (this.isLoading || !this.devicesHasNextPage) {
        return;
      }
      this.isLoading = true;
      const data = {
        id: item.id,
        pageSize: this.itemsPerPage,
        page: this.page,
        tenantId: item.tenantId
      };
      const linkedDevices = await ApiService.fetchLinkedVirtualDevices(data);
      this.totalCountOfLinkedDevices = linkedDevices.pagination['total-count'];
      this.linkedDevices.push(...linkedDevices.data);
      if (this.linkedDevices.length >= linkedDevices.pagination['total-count']) {
        this.devicesHasNextPage = false;
      }
      this.isLoading = false;
    },
    handleScroll() {
      const scrollContainer = this.$refs.scrollContainer;
      if (scrollContainer.scrollTop + scrollContainer.clientHeight >= scrollContainer.scrollHeight) {
        this.page += 1;
        this.fetchLinkedVirtualDevices(this.itemToDelete);
      }
    }
  }
};